import { nthArg } from 'lodash';
import { queryPosts } from '@wix/ambassador-blog-v3-post/http';
import { PostFieldField, type Post } from '@wix/ambassador-blog-v3-post/types';
import {
  createAction,
  getTPASettingsBlocksPostPage,
  resolveId,
} from '@wix/communities-blog-client-common';
import { getQueryLocale } from '../../common/selectors/locale-selectors';
import { getDemoPosts } from '../../common/services/demo-posts';
import getEnvironment from '../../common/services/get-environment';
import { isDemoPostHeroImage, normalizePosts } from '../../common/services/post-utils';
import { getCategoryIds } from '../../common/store/categories/categories-selectors';
import { type NormalizedPost } from '../../common/types';
import { type PostPageThunkAction } from '../types';

export const FETCH_RECENT_POSTS_REQUEST = 'recentPosts/FETCH_REQUEST';
export const FETCH_RECENT_POSTS_SUCCESS = 'recentPosts/FETCH_SUCCESS';
export const FETCH_RECENT_POSTS_FAILURE = 'recentPosts/FETCH_FAILURE';

export const fetchRecentPostsRequest = createAction(FETCH_RECENT_POSTS_REQUEST);
export const fetchRecentPostsSuccess = createAction(
  FETCH_RECENT_POSTS_SUCCESS,
  nthArg(0),
  nthArg(1),
);
const fetchRecentPostsFailure = createAction(FETCH_RECENT_POSTS_FAILURE, nthArg(0), nthArg(1));

export default function fetchRecentPosts(post: NormalizedPost, postCount = 3): PostPageThunkAction {
  return async (
    dispatch,
    getState,
    { httpClient, platformApi, aggregatorRequest, errorHandler },
  ) => {
    const params = { postId: resolveId(post) };
    dispatch(fetchRecentPostsRequest(params));
    const lang = getQueryLocale(getState());
    const editor = getEnvironment(platformApi).isEditorSegment;
    const { withErrorHandler, getResolvedError } = errorHandler;
    const isBlocksPostPage = getTPASettingsBlocksPostPage(getState());

    try {
      const response = await withErrorHandler(
        () =>
          httpClient.request(
            queryPosts({
              query: {
                filter: {
                  ...(lang ? { language: lang } : {}),
                  ...(post.categoryIds?.length
                    ? { categoryIds: { $hasSome: post.categoryIds } }
                    : {}),
                  id: { $ne: post.id },
                },
                paging: { offset: 0, limit: postCount },
              },
              fieldsets: [PostFieldField.METRICS],
            }),
          ),
        { errorCodesMap: {} },
      );

      let posts = response.data.posts as Post[];

      if (!posts.length && editor) {
        const fake = await getDemoPosts({
          httpClient,
          getState,
          query: { pageSize: postCount + 1, excludeIds: [post.id!] },
          errorHandler,
        });

        posts = fake.posts as Post[];
      }

      let normalizedPosts = normalizePosts({
        posts,
        blogCategoryIds: getCategoryIds(getState()),
        origin: '/v3/posts',
      });

      if (!isBlocksPostPage) {
        normalizedPosts = normalizedPosts.map((x) => ({
          ...x,
          heroImage: isDemoPostHeroImage(x.heroImage) ? undefined : x.heroImage,
        }));
      }

      dispatch(fetchRecentPostsSuccess(normalizedPosts, params));
    } catch (error) {
      getResolvedError(error);
      dispatch(fetchRecentPostsFailure(undefined, params));
    }
  };
}
